<template>
  <div :class="[$style.oneXP, error && $style.error, $style.select]">
    <template v-if="useTeleport">
      <div
        :id="identifier"
        ref="triggerRef"
        class="multiselect"
        :class="[$style.teleportTrigger, isClosed && $style.closed]"
        tabindex="0"
        @click="toggleTeleport"
      >
        <div class="multiselect__select"></div>
        <div class="multiselect__tags">
          <span class="multiselect__single">
            {{ value }}
          </span>
        </div>
      </div>
      <Teleport to="body">
        <div
          v-if="isTeleportOpen"
          :class="[$style.select, $style.oneXP]"
          :style="teleportContainerStyle"
        >
          <div class="multiselect__content-wrapper" style="max-height: 20rem; overflow-y: auto">
            <ul class="multiselect__content" style="list-style: none; margin: 0; padding: 0">
              <li v-for="(opt, i) in optionsRef" :key="i" class="multiselect__element">
                <span
                  class="multiselect__option"
                  :class="[value === opt && 'multiselect__option--selected']"
                  @click="selectTeleportOption(opt)"
                >
                  <span>{{ opt }}</span>
                  <span v-if="value === opt" class="multiselect__option--selected-icon"></span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Teleport>
    </template>
    <VueMultiselect
      v-else-if="isMulti"
      :id="identifier"
      ref="multiSelectRef"
      v-model="multiValue"
      :allow-empty="allowEmpty"
      :disabled="disabled"
      :index="index"
      :label="label"
      :multiple="true"
      :options="optionsRef"
      :placeholder="placeholder || label"
      :searchable="false"
      :show-labels="false"
      :track-by="trackBy"
      @remove="handleRemoveLastValue(multiValue.length)"
    >
      <template #tag="selectProps">
        <span class="multiselect__tag">
          <span v-html="selectProps.option" />
          <i class="multiselect__tag-icon" @click="selectProps.remove(selectProps.option)" />
        </span>
      </template>
      <template #singleLabel="selectProps">
        <span v-html="getOptionLabel(selectProps.option)" />
      </template>
      <template #option="selectProps">
        <span v-html="getOptionLabel(selectProps.option)" />
      </template>
    </VueMultiselect>
    <VueMultiselect
      v-else
      :id="identifier"
      ref="singleSelectRef"
      v-model="value"
      :allow-empty="allowEmpty"
      class="single"
      :disabled="disabled"
      :index="index"
      :label="label"
      :options="optionsRef"
      :placeholder="placeholder"
      :searchable="searchable"
      :show-labels="false"
      :track-by="trackBy"
      @close="onClose()"
    >
      <template #singleLabel="{ option }">
        <div v-if="option.icon && option.icon === 'worldwide'" class="single__search-icon">
          <span class="material-symbols-outlined Icon__size-medium">language</span>
          <span>Worldwide</span>
        </div>
        <div v-else-if="option.icon" class="single__search-icon">
          <img v-if="option.icon" alt="" :src="option.icon" />
          <span>{{ option[props.label] }}</span>
        </div>
        <div v-else>
          <span v-html="getOptionLabel(option)" />
        </div>
      </template>
      <template #option="selectProps">
        <span v-html="getOptionLabel(selectProps.option)" />
      </template>
    </VueMultiselect>
  </div>
</template>

<script setup>
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  selectValue: {
    type: [String, Object],
    default: '',
  },
  selectMultiValue: {
    type: [String, Array],
    default: () => [],
  },
  selectOptions: {
    type: Array,
    default: () => [],
  },
  selectIndex: {
    type: String,
    default: '',
  },
  trackBy: {
    type: String,
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  isMulti: {
    type: Boolean,
    default: false,
  },
  category: {
    type: String,
    default: undefined,
  },
  activeSelect: {
    type: String,
    default: undefined,
  },
  resetMultiSelects: {
    Type: Boolean,
    default: false,
  },
  searchable: {
    Type: Boolean,
    default: false,
  },
  allowEmpty: {
    Type: Boolean,
    default: true,
  },
  disabled: {
    Type: Boolean,
    default: false,
  },
  error: {
    Type: Boolean,
    default: false,
  },
  useTeleport: {
    type: Boolean,
    default: false,
  },
  identifier: {
    type: String,
    optional: true,
  },
});

const emit = defineEmits(['onChange', 'multiChanged', 'isEmpty']);

const value = ref('');
const multiValue = ref([]);
const optionsRef = ref([]);
const index = ref(props.selectIndex);
const singleSelectRef = ref(null);
const multiSelectRef = ref(null);
const isClosed = ref(false);

// Teleport Logic
const isTeleportOpen = ref(false);
const triggerRef = ref(null);
const teleportContainerStyle = ref({});

const toggleTeleport = () => {
  if (isTeleportOpen.value) {
    closeTeleport();
  } else {
    openTeleport();
  }
};

const openTeleport = () => {
  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    teleportContainerStyle.value = {
      position: 'fixed',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: '2147483647',
      backgroundColor: '#ffffff',
    };
    isTeleportOpen.value = true;
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
  }
};
const closeTeleport = () => {
  isTeleportOpen.value = false;
  window.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
};

const updatePosition = () => {
  if (isTeleportOpen.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    teleportContainerStyle.value = {
      position: 'fixed',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: '2147483647',
      backgroundColor: '#ffffff',
    };
  }
};

const handleClickOutside = (event) => {
  if (triggerRef.value && !triggerRef.value.contains(event.target)) {
    const clickedInsideDropdown = event.target.closest('.multiselect__content-wrapper');
    if (!clickedInsideDropdown) {
      closeTeleport();
    }
  }
};

const selectTeleportOption = (opt) => {
  value.value = opt;
  emit('onChange', opt);
  closeTeleport();
};
// End Teleport Logic

const getOptionLabel = (option) => {
  if (typeof option === 'object') {
    if ('icon' in option) {
      return option.icon === 'worldwide'
        ? `<div class="single__search-icon"> <span class="material-symbols-outlined Icon__size-medium">language</span> <span class="multiselect__option-label">Worldwide</span> </div>`
        : option.icon
          ? `<div class="single__search-icon"> <img src="${option.icon}" alt="" class="multiselect__option-label" />${option[props.label]} </div>`
          : `<span class="option-no-icon">${option[props.label]}</span>`;
    } else {
      return option[props.label];
    }
  }
  return option;
};

const handleRemoveLastValue = (length) => {
  if (length === 0) {
    multiValue.value = [];
    multiValue.value.push(optionsRef.value[0] || props.selectOptions[0]);
  }
};

const onClose = () => {
  return (isClosed.value = true);
};

watch(value, (newValue, oldValue) => {
  if (newValue !== oldValue && !props.isMulti) {
    if (!newValue) {
      if (props.resetMultiSelects) {
        value.value = '';
      } else {
        value.value = oldValue;
      }
    } else {
      emit('onChange', newValue);
    }
  }
});

watch(multiValue, (newValue, oldValue) => {
  if (newValue !== oldValue && props.isMulti) {
    if (newValue.length === 0) {
      multiValue.value.push(optionsRef.value[0]);
    } else if (newValue.length > 1) {
      if (newValue[0] === optionsRef.value[0]) {
        multiValue.value.shift();
      } else if (newValue.includes(optionsRef.value[0])) {
        multiValue.value = [optionsRef.value[0]];
      }
    }
    emit('multiChanged', multiValue.value);
  }
});

watch(
  () => props.selectValue,
  (newValue, oldValue) => {
    if (!props.isMulti) {
      if (newValue !== oldValue && newValue) {
        return (value.value = Array.isArray(props.selectValue)
          ? props.selectValue[0]
          : props.selectValue);
      }
      emit('onChange', value.value);
    }
  },
);

watch(
  () => props.resetMultiSelects,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      if (props.isMulti) {
        multiValue.value = [];
      } else if (value.value) {
        value.value = '';
      }
    }
  },
);

watch(isClosed, () => {
  if (isClosed.value && !props.allowEmpty) {
    value.value ? emit('isEmpty', false) : emit('isEmpty', true);
    isClosed.value = false;
  }
});

watch(
  () => props.selectOptions,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      optionsRef.value = props.selectOptions;
    }
  },
);

onMounted(() => {
  optionsRef.value = props.selectOptions;
  if (props.isMulti) {
    multiValue.value = props.selectMultiValue;
  } else {
    value.value = props.selectValue;
  }
});

onBeforeUnmount(() => {
  optionsRef.value = [];
  value.value = '';
  multiValue.value = [];
});
</script>

<style module lang="scss">
@forward './Select.scss';
</style>
